import IconChevronDown from '@mnd-frontend/icons/components/ChevronDown';
import {
  WEB_FONT_FORM_FIELD_ERROR,
  WEB_FONT_FORM_FIELD_INPUT,
  WEB_FONT_FORM_FIELD_LABEL,
  WEB_GREY_REG,
  WEB_PURPLE_EXTRA_LIT,
  WEB_PURPLE_REG,
  WEB_RED_REG,
} from '@mnd-frontend/ui/shared/tokens';
import { useId } from 'react';
import styled, { css } from 'styled-components';
import type { SelectProps } from './index.type';

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  color: ${WEB_PURPLE_REG};
  font-weight: 600;
  font-size: ${WEB_FONT_FORM_FIELD_LABEL};
  margin-bottom: 0.5rem;
  padding-left: 1rem;
`;

const RequiredAsterisk = styled.span`
  color: ${WEB_RED_REG};
  font-weight: 400;
  margin-left: 0.125rem;
`;

const LabelDescription = styled.span`
  color: ${WEB_PURPLE_REG};
  font-weight: 400;
`;

const SelectWrapper = styled.div`
  position: relative;
`;

const StyledSelect = styled.select<{
  $hasError: boolean;
}>`
  appearance: none;
  width: 100%;
  border: 0;
  border-radius: 6.25rem;
  ${({ $hasError }) =>
    $hasError &&
    css`
      outline: 2px solid ${WEB_RED_REG};
    `}
  background: rgba(0, 0, 0, 0.05);
  padding: 0.5rem 2.5rem 0.5rem 1rem;
  color: ${WEB_PURPLE_REG};
  &:has(option[value=""]:checked) {
    color: ${WEB_GREY_REG};
  }
  font-size: ${WEB_FONT_FORM_FIELD_INPUT};
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  &:focus {
    outline: 2px solid ${WEB_PURPLE_EXTRA_LIT};
  }
`;

const Chevron = styled.span`
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  display: flex;
  align-items: center;
  color: ${WEB_PURPLE_REG};
`;

const ErrorText = styled.div`
  color: ${WEB_RED_REG};
  font-size: ${WEB_FONT_FORM_FIELD_ERROR};
  padding-left: 1rem;
  margin-top: 0.25rem;
`;

export const Select = ({
  value,
  name,
  onBlur,
  onChange,
  error,
  label,
  labelDescription,
  required,
  placeholder,
  options,
}: SelectProps) => {
  const selectId = useId();
  const errorTextId = useId();

  return (
    <InputWrapper>
      {label && (
        <Label htmlFor={selectId}>
          {label}
          {labelDescription && (
            <>
              {' '}
              <LabelDescription>({labelDescription})</LabelDescription>
            </>
          )}
          {required && <RequiredAsterisk>*</RequiredAsterisk>}
        </Label>
      )}
      <SelectWrapper>
        <StyledSelect
          id={selectId}
          name={name}
          value={value ?? undefined}
          onChange={onChange}
          onBlur={onBlur}
          $hasError={!!error}
          required={required}
          aria-invalid={!!error}
          aria-errormessage={errorTextId}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map(opt => (
            <option key={opt.value} value={opt.value} disabled={opt.value === null}>
              {opt.label}
            </option>
          ))}
        </StyledSelect>
        <Chevron>
          <IconChevronDown color={WEB_PURPLE_REG} />
        </Chevron>
      </SelectWrapper>
      {error && <ErrorText id={errorTextId}>{error}</ErrorText>}
    </InputWrapper>
  );
};

export default Select;
